import { inject, Injectable } from '@angular/core';
import { defer, map, Observable, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { AuthResponse } from '../models/auth-response.interface';
import { LoginBodyInterface } from '../models/login-body.interface';
import { ApiResultInterface, ApiService, CacheService, RequestBuilder, RequestTypeEnum } from '@client-monorepo/common/network';
import { DeviceInfoService, MessageService, StorageService } from '@client-monorepo/common/utilities';
import { SendSmsRequest, SendSmsResponse } from '../models/send-sms-interface';
import { VerifyOtpRequest, VerifyOtpResponse } from '../models/verify-otp.interface';
import { NgxApiConfigService } from '@digipay/ngx-api-config';
import { UserDataService } from './user-data.service';
import { NgxHybridService } from '@digipay/ngx-hybrid-service';
import moment from 'jalali-moment';
import { SessionsResponse } from '../models/sessions-response';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  ngxApiConfigService = inject(NgxApiConfigService);
  deviceInfoService = inject(DeviceInfoService);
  router = inject(Router);
  apiService = inject(ApiService);
  storageService = inject(StorageService);
  cacheService = inject(CacheService);
  userDataService = inject(UserDataService);
  private ngxHybridService = inject(NgxHybridService);
  private messageService = inject(MessageService);

  public refreshToken(): Observable<AuthResponse> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'users/token/refresh', {
      refreshToken: this.storageService.getRefreshToken(),
    });
    return this.apiService.call<AuthResponse>(request);
  }

  public isLoggedIn() {
    return !!this.storageService.getToken();
  }

  public login(params: LoginBodyInterface, requestHeaders?: Record<string, string>): Observable<AuthResponse> {
    const basicHeader = this.ngxApiConfigService.getBasicAuthHeader();
    const header = { Authorization: basicHeader, ...requestHeaders };
    let request = new RequestBuilder(RequestTypeEnum.POST, 'users/login', params);
    request = request.setHeader(header);
    return this.apiService.call<AuthResponse>(request);
  }

  public logout(): void {
    this.logoutUserApi().subscribe({
      next: () => this.performLocalLogout(),
      error: (err) => this.messageService.showErrorOfErrorResponse(err),
    });
  }

  public performLocalLogout() {
    localStorage.removeItem('__dp_storage');
    localStorage.removeItem('__wealth_token');
    localStorage.removeItem('userId');
    sessionStorage.clear();
    this.cacheService.flushCache();
    this.storageService.removePasswordData();
    this.storageService.removeHasBiometric();
    this.storageService.removeTimeStamp();
    this.ngxHybridService.removePin().then();
    this.router.navigate(['/auth/login']);
    this.userDataService.userDetail.next(null);
  }

  public sendSms(params: SendSmsRequest): Observable<SendSmsResponse> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'users/send-sms', params);
    return this.apiService.call<SendSmsResponse>(request);
  }

  verifyOtp(params: VerifyOtpRequest): Observable<VerifyOtpResponse> {
    const basicHeader = this.ngxApiConfigService.getBasicAuthHeader();
    const header = { Authorization: basicHeader };
    let request = new RequestBuilder(RequestTypeEnum.POST, 'users/activate', params);
    request = request.setHeader(header);
    return this.apiService.call(request);
  }

  setUserPassword(password: string): Observable<ApiResultInterface> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'users/password', {
      password,
    });
    return this.apiService.call<ApiResultInterface>(request);
  }

  deleteUserPassword(): Observable<ApiResultInterface> {
    const request = new RequestBuilder(RequestTypeEnum.DELETE, 'users/password');
    return this.apiService.call<ApiResultInterface>(request);
  }

  async getCode(phoneNumber: string, referralCode: string | undefined = undefined): Promise<Observable<SendSmsResponse>> {
    const device = await this.deviceInfoService.getDeviceInfo();
    const passToServer: SendSmsRequest = {
      cellNumber: phoneNumber,
      device: device,
    };
    if (referralCode) {
      passToServer.referralCode = referralCode;
    }
    return this.sendSms(passToServer).pipe(tap((res) => this.storageService.setUserData({ userId: res.userId, phoneNumber })));
  }

  verifyReferral(code: string): Observable<{ result: ApiResultInterface }> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'campaigns/referral/verify', { referralCode: code });
    return this.apiService.call<{ result: ApiResultInterface }>(request);
  }

  logoutUserApi(): Observable<ApiResultInterface> {
    return defer(() => this.deviceInfoService.getDeviceInfo()).pipe(
      switchMap((device) => {
        const request = new RequestBuilder(RequestTypeEnum.POST, 'users/logout', { ...device });
        return this.apiService.call<ApiResultInterface>(request);
      }),
    );
  }

  getSessionsData(): Observable<SessionsResponse> {
    const request = new RequestBuilder(RequestTypeEnum.GET, 'auth/token/clients');
    return this.apiService.call<SessionsResponse>(request).pipe(
      map((res) => {
        res.clients.forEach((client) => {
          const value = new Date(client.loginTime!);
          client.loginTime = moment(value).locale('fa').format('YYYY/M/D -  HH:mm');
        });
        return res;
      }),
    );
  }

  revokeSession(deviceId: string): Observable<{ result: ApiResultInterface }> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'auth/token/revoke', { deviceId });
    return this.apiService.call<{ result: ApiResultInterface }>(request);
  }

  revokeOtherSessions(excludedDeviceIds: string[]): Observable<{ result: ApiResultInterface }> {
    const request = new RequestBuilder(RequestTypeEnum.POST, 'auth/token/revoke-all', { excludedDeviceIds });
    return this.apiService.call<{ result: ApiResultInterface }>(request);
  }
}
